import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import MainLayout from '../../components/layout/MainLayout';
import Pagination from '../../components/common/Pagination';
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

import {
  BuildingOfficeIcon,
  UserGroupIcon,
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface DataStatistics {
  logs: number;
  traces: number;
  spans: number;
  rumSessions: number;
  rumEvents: number;
  recordings: number;
  metricDataPoints: number;
  uptimeChecks: number;
  errorOccurrences: number;
  totalRecords: number;
  storageEstimateGB: number;
}

interface RetentionInfo {
  maxRetentionDays: number;
  currentRetentionDays: number;
  planName: string;
}

interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  is_active: boolean;
  created_at: string;
  subscription?: {
    id: string;
    status: string;
    plan: {
      name: string;
      tier: string;
    };
    billing_cycle: string;
    current_period_end: string;
    trial_ends_at: string | null;
  };
  users: Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  }>;
}

export default function TenantsPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'trialing' | 'canceled'>('all');
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', subdomain: '', status: 'active' });
  const [isSaving, setIsSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Data retention and statistics state
  const [dataStatistics, setDataStatistics] = useState<DataStatistics | null>(null);
  const [retentionInfo, setRetentionInfo] = useState<RetentionInfo | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [retentionDays, setRetentionDays] = useState<number>(30);
  const [savingRetention, setSavingRetention] = useState(false);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [purgeConfirmation, setPurgeConfirmation] = useState('');
  const [isPurging, setIsPurging] = useState(false);

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/tenants`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load tenants');
      }

      const data = await response.json();

      if (data.success) {
        setTenants(data.data.tenants);
      }
    } catch (error) {
      console.error('Failed to load tenants', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTenants = tenants.filter((tenant) => {
    const matchesSearch =
      tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.subdomain.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.users.some((u) => u.email.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter =
      filterStatus === 'all' ||
      (filterStatus === 'active' && tenant.subscription?.status === 'ACTIVE' && !tenant.subscription.trial_ends_at) ||
      (filterStatus === 'trialing' && tenant.subscription?.status === 'TRIALING') ||
      (filterStatus === 'canceled' && tenant.subscription?.status === 'CANCELED');

    return matchesSearch && matchesFilter;
  });

  // Paginate results
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTenants = filteredTenants.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const getStatusBadge = (subscription: Tenant['subscription']) => {
    if (!subscription) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">No Subscription</span>;
    }

    if (subscription.status === 'TRIALING') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">Trial</span>;
    }

    if (subscription.status === 'ACTIVE') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Active</span>;
    }

    if (subscription.status === 'CANCELED') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">Canceled</span>;
    }

    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">{subscription.status}</span>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleEditClick = (tenant: Tenant) => {
    setEditForm({
      name: tenant.name,
      subdomain: tenant.subdomain,
      status: tenant.is_active ? 'active' : 'inactive',
    });
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedTenant) return;

    try {
      setIsSaving(true);
      const response = await fetch(`${API_BASE_URL}/v1/admin/tenants/${selectedTenant.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editForm),
      });

      const data = await response.json();

      if (data.success) {
        // Update tenant in list
        setTenants(tenants.map(t => t.id === selectedTenant.id ? {
          ...t,
          name: editForm.name,
          subdomain: editForm.subdomain,
          is_active: editForm.status === 'active',
        } : t));

        // Update selected tenant
        setSelectedTenant({
          ...selectedTenant,
          name: editForm.name,
          subdomain: editForm.subdomain,
          is_active: editForm.status === 'active',
        });

        setIsEditing(false);
        toast.success('Tenant updated successfully!', {
          duration: 3000,
          icon: '✅',
        });
      } else {
        toast.error(data.error || 'Failed to update tenant', {
          duration: 5000,
          icon: '❌',
        });
      }
    } catch (error) {
      console.error('Failed to update tenant', error);
      toast.error('Error updating tenant', {
        duration: 5000,
        icon: '❌',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (selectedTenant) {
      setEditForm({
        name: selectedTenant.name,
        subdomain: selectedTenant.subdomain,
        status: selectedTenant.is_active ? 'active' : 'inactive',
      });
    }
  };

  // Load data statistics when tenant is selected
  useEffect(() => {
    if (selectedTenant) {
      // Reset state before loading new data
      setDataStatistics(null);
      setRetentionInfo(null);
      setRetentionDays(30);
      loadTenantDataStatistics(selectedTenant.id);
    }
  }, [selectedTenant?.id]);

  const loadTenantDataStatistics = async (tenantId: string) => {
    setLoadingStats(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/tenants/${tenantId}/data-statistics`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load data statistics');

      const data = await response.json();
      if (data.success) {
        setDataStatistics(data.data.statistics);
        setRetentionInfo(data.data.retention);
        setRetentionDays(data.data.retention.currentRetentionDays);
      }
    } catch (error) {
      console.error('Failed to load tenant data statistics', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleSaveRetention = async () => {
    if (!selectedTenant) return;

    setSavingRetention(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/tenants/${selectedTenant.id}/retention`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dataRetentionDays: retentionDays }),
      });

      const data = await response.json();
      if (data.success) {
        setRetentionInfo(data.data);
        toast.success('Retention settings saved successfully');
      } else {
        toast.error(data.error || 'Failed to save retention settings');
      }
    } catch (error) {
      console.error('Failed to save retention settings', error);
      toast.error('Failed to save retention settings');
    } finally {
      setSavingRetention(false);
    }
  };

  const handlePurgeData = async () => {
    if (!selectedTenant || purgeConfirmation !== selectedTenant.id) {
      toast.error('Please enter the tenant ID to confirm');
      return;
    }

    setIsPurging(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/tenants/${selectedTenant.id}/purge-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ confirmation: selectedTenant.id }),
      });

      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        setShowPurgeModal(false);
        setPurgeConfirmation('');
        // Reload statistics
        loadTenantDataStatistics(selectedTenant.id);
      } else {
        toast.error(data.error || 'Failed to purge data');
      }
    } catch (error) {
      console.error('Failed to purge data', error);
      toast.error('Failed to purge data');
    } finally {
      setIsPurging(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Tenants & Subscribers</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Manage all tenant organizations and their subscriptions
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tenants</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{tenants.length}</p>
              </div>
              <BuildingOfficeIcon className="h-12 w-12 text-indigo-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Subscriptions</p>
                <p className="text-3xl font-bold text-green-600 mt-2">
                  {tenants.filter((t) => t.subscription?.status === 'ACTIVE').length}
                </p>
              </div>
              <CheckCircleIcon className="h-12 w-12 text-green-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">In Trial</p>
                <p className="text-3xl font-bold text-blue-600 mt-2">
                  {tenants.filter((t) => t.subscription?.status === 'TRIALING').length}
                </p>
              </div>
              <ClockIcon className="h-12 w-12 text-blue-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Canceled</p>
                <p className="text-3xl font-bold text-red-600 mt-2">
                  {tenants.filter((t) => t.subscription?.status === 'CANCELED').length}
                </p>
              </div>
              <XCircleIcon className="h-12 w-12 text-red-600" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, subdomain, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="trialing">In Trial</option>
                <option value="canceled">Canceled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tenants List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Tenant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Subscription
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedTenants.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    No tenants found
                  </td>
                </tr>
              ) : (
                paginatedTenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="h-10 w-10 text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{tenant.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{tenant.subdomain}.devskin.io</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {tenant.subscription ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {tenant.subscription.plan.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {tenant.subscription.billing_cycle === 'MONTHLY' ? 'Monthly' : 'Annual'}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(tenant.subscription)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900 dark:text-white">{tenant.users.length}</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{tenant.users[0]?.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(tenant.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                        onClick={() => setSelectedTenant(tenant)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {filteredTenants.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalItems={filteredTenants.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </div>
      </div>

      {/* Tenant Detail Modal */}
      {selectedTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedTenant(null)}>
          <div className="max-w-4xl w-full rounded-lg bg-white p-6 dark:bg-gray-800 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                Tenant Details
              </h3>
              <button
                onClick={() => setSelectedTenant(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Info */}
              <div className="border-b pb-4 dark:border-gray-700">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h4>
                {isEditing ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Organization Name
                      </label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Subdomain
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editForm.subdomain}
                          onChange={(e) => setEditForm({ ...editForm, subdomain: e.target.value })}
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        />
                        <span className="text-sm text-gray-500">.devskin.io</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Status
                      </label>
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Created
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white py-2">{formatDate(selectedTenant.created_at)}</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Organization Name
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{selectedTenant.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Subdomain
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{selectedTenant.subdomain}.devskin.io</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Status
                      </label>
                      <p className="text-sm">
                        {selectedTenant.is_active ? (
                          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                            Inactive
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Created
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(selectedTenant.created_at)}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Subscription Info */}
              {selectedTenant.subscription && (
                <div className="border-b pb-4 dark:border-gray-700">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Subscription</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Plan
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{selectedTenant.subscription.plan.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Billing Cycle
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {selectedTenant.subscription.billing_cycle === 'MONTHLY' ? 'Monthly' : 'Annual'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Status
                      </label>
                      <p className="text-sm">{getStatusBadge(selectedTenant.subscription)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Current Period End
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {formatDate(selectedTenant.subscription.current_period_end)}
                      </p>
                    </div>
                    {selectedTenant.subscription.trial_ends_at && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Trial Ends
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {formatDate(selectedTenant.subscription.trial_ends_at)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Users */}
              <div className="border-b pb-4 dark:border-gray-700">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Users ({selectedTenant.users.length})
                </h4>
                <div className="space-y-2">
                  {selectedTenant.users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between rounded-lg bg-gray-50 p-3 dark:bg-gray-700">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                      </div>
                      <UserGroupIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Data Retention */}
              <div className="border-b pb-4 dark:border-gray-700">
                <div className="flex items-center mb-4">
                  <ClockIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Data Retention</h4>
                </div>
                {loadingStats ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
                  </div>
                ) : retentionInfo && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Retention Period (days)
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={7}
                          value={retentionDays}
                          onChange={(e) => setRetentionDays(Math.max(7, parseInt(e.target.value) || 7))}
                          className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-center dark:bg-gray-700 dark:text-white"
                        />
                        <button
                          onClick={handleSaveRetention}
                          disabled={savingRetention || retentionDays === retentionInfo.currentRetentionDays}
                          className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 text-sm"
                        >
                          {savingRetention ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 dark:text-gray-400">Plan: {retentionInfo.planName}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Max Allowed: {retentionInfo.maxRetentionDays} days</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Data Statistics */}
              <div className="border-b pb-4 dark:border-gray-700">
                <div className="flex items-center mb-4">
                  <ChartBarIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Data Statistics</h4>
                </div>
                {loadingStats ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
                  </div>
                ) : dataStatistics && (
                  <>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3">
                        <p className="text-xs text-indigo-600 dark:text-indigo-400">Total Records</p>
                        <p className="text-lg font-bold text-indigo-900 dark:text-indigo-100">{formatNumber(dataStatistics.totalRecords)}</p>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                        <p className="text-xs text-green-600 dark:text-green-400">Storage</p>
                        <p className="text-lg font-bold text-green-900 dark:text-green-100">{dataStatistics.storageEstimateGB} GB</p>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                        <p className="text-xs text-purple-600 dark:text-purple-400">Recordings</p>
                        <p className="text-lg font-bold text-purple-900 dark:text-purple-100">{formatNumber(dataStatistics.recordings)}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-sm">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                        <p className="text-xs text-gray-500">Logs</p>
                        <p className="font-medium dark:text-white">{formatNumber(dataStatistics.logs)}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                        <p className="text-xs text-gray-500">Traces</p>
                        <p className="font-medium dark:text-white">{formatNumber(dataStatistics.traces)}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                        <p className="text-xs text-gray-500">Spans</p>
                        <p className="font-medium dark:text-white">{formatNumber(dataStatistics.spans)}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                        <p className="text-xs text-gray-500">RUM Sessions</p>
                        <p className="font-medium dark:text-white">{formatNumber(dataStatistics.rumSessions)}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                        <p className="text-xs text-gray-500">RUM Events</p>
                        <p className="font-medium dark:text-white">{formatNumber(dataStatistics.rumEvents)}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                        <p className="text-xs text-gray-500">Metrics</p>
                        <p className="font-medium dark:text-white">{formatNumber(dataStatistics.metricDataPoints)}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                        <p className="text-xs text-gray-500">Uptime</p>
                        <p className="font-medium dark:text-white">{formatNumber(dataStatistics.uptimeChecks)}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                        <p className="text-xs text-gray-500">Errors</p>
                        <p className="font-medium dark:text-white">{formatNumber(dataStatistics.errorOccurrences)}</p>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Danger Zone */}
              <div className="border-b pb-4 dark:border-gray-700">
                <div className="flex items-center mb-4">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                  <h4 className="text-lg font-semibold text-red-600">Danger Zone</h4>
                </div>
                <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/50 rounded-lg p-4">
                  <p className="text-sm text-red-800 dark:text-red-400 mb-3">
                    Permanently delete all observability data for this tenant. This action cannot be undone.
                  </p>
                  <button
                    onClick={() => setShowPurgeModal(true)}
                    className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Purge All Data
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 border-t pt-4 dark:border-gray-700">
                {isEditing ? (
                  <>
                    <button
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                      className="rounded-md px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      disabled={isSaving}
                      className="rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {isSaving && (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      )}
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setSelectedTenant(null);
                        setIsEditing(false);
                      }}
                      className="rounded-md px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Close
                    </button>
                    <button
                      onClick={() => handleEditClick(selectedTenant)}
                      className="rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
                    >
                      Edit Tenant
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purge Confirmation Modal */}
      {showPurgeModal && selectedTenant && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="max-w-md w-full rounded-lg bg-white p-6 dark:bg-gray-800">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Purge All Data</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{selectedTenant.name}</p>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                This will permanently delete <strong>all observability data</strong> for this tenant including logs, traces, RUM sessions, recordings, metrics, and more. This action cannot be undone.
              </p>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Type the tenant ID to confirm: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{selectedTenant.id}</code>
              </label>
              <input
                type="text"
                value={purgeConfirmation}
                onChange={(e) => setPurgeConfirmation(e.target.value)}
                placeholder="Enter tenant ID"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPurgeModal(false);
                  setPurgeConfirmation('');
                }}
                disabled={isPurging}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-md disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePurgeData}
                disabled={isPurging || purgeConfirmation !== selectedTenant.id}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isPurging && (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                )}
                {isPurging ? 'Purging...' : 'Purge All Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
